import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dismissJobRecommendations } from '@worker/services/MyJobs/api';
import { JobRecommendationResponse } from '@worker/types/myJobs.type';
import { extractErrorMessage } from '@worker/helpers/my-jobs-helpers';
import QueryKeys from '@constants/queryKeys';
import useNotification from '@hooks/useNotification';
import { TodoResponse, TodoType } from '@th-types/inbox-reviews.type';
import { useMyJobsQueryKey } from './useMyJobsQueryKey';

const TODOS_QUERY_KEY = [QueryKeys.WorkerInbox.TODOS];
const INDICATORS_QUERY_KEY = [QueryKeys.WorkerInbox.INDICATORS];

const DISMISSABLE_TODO_TYPES = new Set<TodoType>([
  TodoType.RECOMMENDED_JOB,
  TodoType.COMPANY_SHARED_JOB,
]);

export function useDismissJobMutation() {
  const queryClient = useQueryClient();
  const queryKey = useMyJobsQueryKey();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  return useMutation({
    mutationKey: [QueryKeys.MyJobs.JOB_RECOMMENDATIONS, 'dismiss'],
    mutationFn: (jobRecommendationId: number) =>
      dismissJobRecommendations([jobRecommendationId]),
    onSuccess: (_data, jobRecommendationId) => {
      queryClient.setQueryData<JobRecommendationResponse>(
        queryKey,
        (oldData) => {
          if (!oldData?.results) return oldData;

          const newCount = Math.max(0, (oldData.pagination?.count || 0) - 1);
          return {
            ...oldData,
            pagination: { ...oldData.pagination, count: newCount },
            results: oldData.results.filter(
              (result) => result.jobRecommendation?.id !== jobRecommendationId
            ),
          };
        }
      );

      // Patch inbox TODOS also to avoid recalculation in BE.
      queryClient.setQueryData<TodoResponse>(TODOS_QUERY_KEY, (prev) => {
        if (!prev) return prev;
        const nextList = prev.list.filter(
          (todo) =>
            !(
              todo.jobRecommendationId === jobRecommendationId &&
              DISMISSABLE_TODO_TYPES.has(todo.type)
            )
        );
        if (nextList.length === prev.list.length) return prev;
        return {
          ...prev,
          count: nextList.length,
          list: nextList,
        };
      });

      queryClient.invalidateQueries({ queryKey: INDICATORS_QUERY_KEY });

      showSuccessNotification('Job dismissed!', {
        title: 'Dismissed',
      });
    },
    onError: (err: unknown) => {
      showErrorNotification(
        extractErrorMessage(err, 'Failed to dismiss job. Please try again.')
      );
    },
  });
}
